import * as Business   from '../business';
import * as Forms      from '../components/forms';
import * as Phone      from 'libphonenumber-js';
import * as UI         from '../ui/actions';
import * as User       from '../user';
import { compose }     from 'redux';
import { connect }     from 'react-redux';
import { useEffect }   from 'react';
import { useState }    from 'react';
import FetchFromServer from '../components/fetch_from_server';
import Link            from '../components/link';
import Navbar          from '../components/navbar';
import PrimaryButton   from '../components/primary_button';
import React           from 'react';
import restrictAccess  from '../components/restrict_access';
import rollbar         from '../rollbar';


export default connect(
  (state, { businessID }) => ({
    businessID,
    business:      Business.getBusiness(state, businessID),
    currentNumber: Business.getConnectedPhoneNumber(state, businessID),
    hostedNumber:  Business.getHostedNumber(state, businessID),
    isAdmin:       User.isAdmin(state, businessID),
    statuses:      Business.getBusinessStatuses(state, businessID),
    user:          User.getProfile(state)
  }),
  (dispatch, { businessID }) => {
    async function load() {
      await dispatch(Business.loadBusiness(businessID));
    }

    return {
      load,
      silentlyLoad:              () => dispatch(Business.silentlyLoadBusiness(businessID)),
      progressStart:             () => dispatch(UI.progressStart()),
      progressEnd:               () => dispatch(UI.progressEnd()),
      recreateHostedNumberOrder: compose(dispatch, Business.recreateHostedNumberOrder, ({ orderSid, phone }) => ({ businessID, orderSid, phone })),
      async verificationCall({ orderSid, extension }) {
        const result = await dispatch(Business.verificationCall({ businessID, orderSid, extension }));
        await load();
        return result;
      },
      async requestLOA({ orderSid, ...fields }) {
        const result = await dispatch(Business.requestLOA({ businessID, orderSid, ...fields }));
        await load();
        return result;
      }
    };
  }
)(restrictAccess(HostedNumberPage, ({ isAdmin }) => isAdmin));

export function HostedNumberPage({
  business,
  load,
  statuses,
  hostedNumber,
  progressStart,
  progressEnd,
  verificationCall,
  requestLOA,
  recreateHostedNumberOrder,
  user,
  silentlyLoad

}) {
  return (
    <React.Fragment>
      <Navbar title="Phone Number"/>
      <FetchFromServer statuses={statuses} load={load}>
        <div className="hosted_number--page block">
          {hostedNumber && <HostedOrderStatus {...{
            business,
            hostedNumber,
            progressStart,
            progressEnd,
            verificationCall,
            requestLOA,
            recreateHostedNumberOrder,
            user,
            silentlyLoad
          }}/>}
        </div>
      </FetchFromServer>
    </React.Fragment>
  );
}

function HostedOrderStatus({
  business,
  hostedNumber,
  progressStart,
  progressEnd,
  verificationCall,
  requestLOA,
  recreateHostedNumberOrder,
  user,
  silentlyLoad
}) {
  const { status }         = hostedNumber;
  const isTwilioProcessing = (status === 'twilio-processing');
  const isVerifyingNumber  = (status === 'received' || status === 'pending-verification');
  const isFillingLOAForm   = (status === 'verified');
  const isPendingSignature = (status === 'pending-loa');
  const isCompleting       = (status === 'carrier-processing' || status === 'testing');
  const isCompleted        = (status === 'completed');
  const isFailed           = (status === 'failed');
  const isActionRequired   = (status === 'action-required');
  const Poller             = createPoller(silentlyLoad);

  return (
    <React.Fragment>
      {isTwilioProcessing && <Poller><TwilioProcessing/></Poller>}

      {isVerifyingNumber && <NumberVerification {...{ Poller, hostedNumber, verificationCall, progressStart, progressEnd }}/>}

      {isFillingLOAForm && <LOAForm {...{ business, hostedNumber, user, requestLOA, progressStart, progressEnd }}/>}

      {isPendingSignature && <Poller><PendingSignature {...{ hostedNumber }}/></Poller>}

      {isCompleting && <Poller><Completing {...{ hostedNumber }}/></Poller>}

      {isCompleted && <Completed {...{ hostedNumber }}/>}

      {isActionRequired && <ActionRequired {...{ business, hostedNumber }}/>}

      {isFailed && <Failed {...{ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }}/>}
    </React.Fragment>
  );

}

function TwilioProcessing() {
  return (
    <p>
      Please wait...
    </p>
  );
}

function NumberVerification({ Poller, hostedNumber, verificationCall, progressStart, progressEnd }) {
  const [ pin, setPIN ]             = useState();
  const [ extension, setExtension ] = useState(null);

  return (
    <React.Fragment>
      {pin && <Poller><PIN {...{ hostedNumber, pin, setPIN, extension }}/></Poller>}
      {!pin && <CallMe {...{ hostedNumber, verificationCall, setPIN, setExtension, progressStart, progressEnd }}/>}
    </React.Fragment>
  );
}


function CallMe({ hostedNumber, verificationCall, setPIN, setExtension, progressStart, progressEnd }) {
  const { number }                        = hostedNumber;
  const { orderSid }                      = hostedNumber;
  const formattedNumber                   = formatPhone(number);
  const [ requestError, setRequestError ] = useState(null);

  async function save({ extension }) {
    try {
      progressStart();
      setRequestError(null);
      setExtension(extension);
      const { verificationCode } = await verificationCall({ orderSid, extension });
      setPIN(verificationCode);
    } catch (error) {
      setRequestError('There was a problem calling your number. Please try again later.');
      throw error;
    } finally {
      progressEnd();
    }
  }

  return (
    <React.Fragment>
      <p>
        We will call <FormattedPhone>{number}</FormattedPhone> to verify that you have access to that number. On the next screen, we will provide a PIN for you to enter.
      </p>

      {requestError && <ErrorMessage error={requestError}/>}

      <Forms.Form {...{ save }}>
        <ExtensionFields/>
      </Forms.Form>

    </React.Fragment>
  );


  function ExtensionFields(form) {
    return (
      <div className="list hosted_number--extension">
        <ul>
          <Forms.Input
            {...form}
            name="extension"
            type="text"
            label="If you have an extension, enter it below."
            placeholder=""
            autoComplete="off"/>
          <PrimaryButton
            label={`Call me at ${formattedNumber}`}
          />
        </ul>
      </div>
    );
  }
}

function ErrorMessage({ error }) {
  return (
    <p className="hosted_number--error_message">
      {error}
    </p>
  );
}

export function PIN({ hostedNumber, pin, setPIN, extension, linkDelay = 10000 }) {
  const { number }                = hostedNumber;
  const formattedNumber           = formatPhone(number);
  const formattedExtension        = extension ? ` ext. ${extension}` : '';
  const [ showLink, setShowLink ] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLink(true);
    }, linkDelay);
    return () => {
      clearTimeout(timer);
    };
  }, [ linkDelay ]);

  return (
    <React.Fragment>
      <p>
        We are now calling <span className="phone-number">{formattedNumber}{formattedExtension}</span>. When prompted, enter the following PIN on your phone keypad:
      </p>
      <div className="hosted_number--pin">
        <input type="text" readOnly value={pin}/>
      </div>
      {showLink && (
        <p>
          Phone not ringing? You can <a href="#" className="request-new-call" onClick={onClick}>request another call</a>.
        </p>
      )}
    </React.Fragment>
  );

  function onClick(event) {
    event.preventDefault();
    setPIN(null);
  }

}


function LOAForm({ business, hostedNumber, progressStart, progressEnd, requestLOA, user }) {
  const defaultRecord = {
    email:        user.email,
    customerName: user.name,
    street:       business.contact.street,
    city:         business.contact.city,
    region:       business.contact.state,
    postalCode:   business.contact.postalCode,
    isoCountry:   business.contact.countryCode
  };

  const { orderSid }                      = hostedNumber;
  const [ record, setRecord ]             = useState(defaultRecord);
  const [ requestError, setRequestError ] = useState(null);

  async function save(data) {
    try {
      setRecord(data);
      progressStart();
      setRequestError(null);
      await requestLOA({ businessID: business.id, orderSid, ...data });
    } catch (error) {
      setRequestError('There was a problem sending your Letter of Authorization. Please try again later.');
      throw error;
    } finally {
      progressEnd();
    }
  }

  function onSaveError() {
    progressEnd();
  }

  return (
    <div className="hosted_number--loa_form">
      <p>
        To complete verification, you must sign a digital Letter of Authorization which will be emailed to you from Broadly’s partner, Twilio.
      </p>
      <p>
        Please complete the information below, exactly as it appears on your telephone bill.
      </p>
      <Forms.Form {...{ record, save, onSaveError }}>
        <LOAFormFields requestError={requestError}/>
      </Forms.Form>
    </div>
  );
}


function LOAFormFields({ requestError, ...form }) {
  return (
    <div className="list">
      <ul>
        <Forms.Error {...form}/>
        <Forms.Input
          {...form}
          name="contactTitle"
          type="text"
          label="Title"
          placeholder="Mr. / Mrs. / Ms."
          required={true}
          autoComplete="off"/>
        <Forms.Input
          {...form}
          name="customerName"
          type="text"
          label="Name"
          required={true}
          autoComplete="off"/>
        <Forms.Input
          {...form}
          name="email"
          type="email"
          label="Email"
          required={true}
          autoComplete="off"/>
        <Forms.Input
          {...form}
          name="street"
          type="text"
          label="Street"
          required={true}
          autoComplete="off"/>
        <Forms.Input
          {...form}
          name="city"
          type="text"
          label="City"
          required={true}
          autoComplete="off"/>
        <Forms.Input
          {...form}
          name="region"
          type="text"
          label="State"
          required={true}
          autoComplete="off"/>
        <Forms.Input
          {...form}
          name="postalCode"
          type="text"
          label="Postal Code"
          required={true}
          autoComplete="off"/>
        <Forms.Input
          {...form}
          name="isoCountry"
          type="text"
          label="Country"
          placeholder="US"
          required={true}
          autoComplete="off"/>
        {requestError && (
          <li><ErrorMessage error={requestError}/></li>
        )}
        <Forms.Button {...form} label="Email me"/>
      </ul>
    </div>
  );
}

function PendingSignature({ hostedNumber }) {
  const { emailTo } = hostedNumber;

  return (
    <p>
      Our partner Twilio has sent an email to <span className="email-address">{emailTo}</span> with your Letter of Authorization. Once you’ve digitally signed this letter, we can finish setting up <FormattedPhone>{hostedNumber.number}</FormattedPhone> for phone calls and SMS messages.
    </p>
  );
}


function Completing({ hostedNumber }) {
  return (
    <p>
      We have received your Letter of Authorization, and we are setting up <FormattedPhone>{hostedNumber.number}</FormattedPhone> for phone calls and SMS messages. We will let you know when setup is complete.
    </p>
  );
}

function Completed({ hostedNumber }) {
  return (
    <React.Fragment>
      <h3>Setup complete!</h3>
      <p>
        Broadly will make phone calls and send and receive SMS messages from your number: <FormattedPhone>{hostedNumber.number}</FormattedPhone>.
      </p>
    </React.Fragment>
  );
}


export const twilioFailureReasons = {
  didNotStartVerification:  'The customer did not start the verification process within 30 days of submitting the Hosted Number Order',
  didNotSendLOA:            'Twilio did not receive a signed LOA from the customer within 30 days after verification was completed',
  maxVerificationAttempts:  'This Hosted Number Order has reached the maximum number of verification attempts',
  cannotHostTollFreeNumber: 'This tollfree number cannot be enabled for Hosted SMS',
  cannotBeEnabled:          'This number cannot be enabled for Hosted SMS',
  vagueReason:              'There was a problem processing your Hosted Number Order'
};

function Failed(props) {
  const failureReason            = (props.hostedNumber.failureReason || '');
  const didNotStartVerification  = failureReason.startsWith(twilioFailureReasons.didNotStartVerification);
  const didNotSendLOA            = failureReason.startsWith(twilioFailureReasons.didNotSendLOA);
  const maxVerificationAttempts  = failureReason.startsWith(twilioFailureReasons.maxVerificationAttempts);
  const cannotHostTollFreeNumber = failureReason.startsWith(twilioFailureReasons.cannotHostTollFreeNumber);
  const cannotBeEnabled          = failureReason.startsWith(twilioFailureReasons.cannotBeEnabled);
  const vagueReason              = failureReason.startsWith(twilioFailureReasons.vagueReason);
  const nullReason               = props.hostedNumber.failureReason === null;

  if (didNotStartVerification)
    return <FailedDidNotStartVerification {...props}/>;
  else if (didNotSendLOA)
    return <FailedDidNotSendLoa {...props}/>;
  else if (maxVerificationAttempts)
    return <FailedMaxVerificationAttempts {...props}/>;
  else if (cannotHostTollFreeNumber || cannotBeEnabled || vagueReason || nullReason)
    return <FailedVagueReason {...props}/>;
  else
    return <FailedUnkownReason {...props}/>;
}

function FailedDidNotStartVerification({ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }) {
  return (
    <React.Fragment>
      <p>
        We were unable to connect <FormattedPhone>{hostedNumber.number}</FormattedPhone> to Broadly for phone calls and SMS messages, because the verification process was not completed in 30 days.
      </p>
      <TryAgainButton {...{ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }}/>
    </React.Fragment>
  );
}

function FailedDidNotSendLoa({ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }) {
  return (
    <React.Fragment>
      <p>
        We were unable to connect <FormattedPhone>{hostedNumber.number}</FormattedPhone> to Broadly for phone calls and SMS messages, because we did not receive your signed Letter of Authorization within 30 days.
      </p>
      <TryAgainButton {...{ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }}/>
    </React.Fragment>
  );
}

function FailedMaxVerificationAttempts({ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }) {
  return (
    <React.Fragment>
      <p>
        We were unable to connect <FormattedPhone>{hostedNumber.number}</FormattedPhone> to Broadly for phone calls and SMS messages, because the maximum number of verification attempts was reached.
      </p>
      <TryAgainButton {...{ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }}/>
    </React.Fragment>
  );
}

function FailedVagueReason({ business, hostedNumber }) {
  return (
    <React.Fragment>
      <p>
        We were unable to connect <FormattedPhone>{hostedNumber.number}</FormattedPhone> to Broadly for phone calls and SMS messages. Please <Link href={`/${business.id}/account/help`}>contact support</Link> for help.
      </p>
    </React.Fragment>
  );
}

function FailedUnkownReason(props) {
  const { hostedNumber }  = props;
  const { failureReason } = hostedNumber;

  useEffect(function() {
    rollbar.error(`Unknown Hosted Order Failure Reason: ${failureReason}`);
  }, [ failureReason ]);

  return (
    <FailedVagueReason {...props}/>
  );
}

function TryAgainButton({ business, hostedNumber, recreateHostedNumberOrder, progressStart, progressEnd }) {
  const { orderSid } = hostedNumber;
  const { phone }    = business.contact;

  async function onClick() {
    progressStart();
    try {
      await recreateHostedNumberOrder({ orderSid, phone });
    } finally {
      progressEnd();
    }
  }

  return (
    <PrimaryButton
      label="Try Again"
      className="try-again-button"
      danger={false}
      onClick={onClick}
    />
  );
}

function ActionRequired({ hostedNumber }) {
  return (
    <p>
      We are setting up <FormattedPhone>{hostedNumber.number}</FormattedPhone> for phone calls and SMS messages. This may take a few days. We’ll let you know when setup is complete.
    </p>
  );
}

function FormattedPhone({ children }) {
  const formattedNumber = formatPhone(children);
  return (
    <span className="phone-number">{formattedNumber}</span>
  );
}

function formatPhone(number) {
  return Phone.format(Phone.parse(number, 'US'), 'National');
}

function createPoller(callback) {
  return function Poller({ children }) {
    useEffect(() => {
      const interval = setInterval(callback, 1000);
      return () => clearInterval(interval);
    }, []);

    return children;
  };
}
